import { Injectable, inject } from '@angular/core';
import { GraphApiService } from '@nuso/common/data-access-api';
import { catchError, Observable, of, Subject, tap } from 'rxjs';
import {
  IntlLocationSearchInput,
  IntlLocationSearchResult,
  LergSearchInput,
  LergSearchResult,
  TnAvailableSearchInput,
  TnAvailableSearchResults,
  TnOrderingReleaseInput,
  TnOrderingReserveInput,
  TnOrderInput,
  TnPortalType,
} from '@nuso/cpaas/types';
import { fetchIntlLocationDataQuery, fetchLergDataQuery, tnAvailableIntlSearchQuery, tnAvailableSearchQuery } from './queries';
import {
  cpaasTnsChangeCustomer,
  tnDisconnectTnsMultiAccount,
  tnModifyTns,
  tnOrder,
  tnRelease,
  tnReserve
} from './mutations';
import { Apollo } from 'apollo-angular';
import { Router } from '@angular/router';
import { NotificationService } from '@nuso/common/util-errors';

@Injectable({
  providedIn: 'root',
})
export class CpaasNumberOrderingService {
  private graphApi = inject(GraphApiService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  public searchInput$ = new Subject<TnAvailableSearchInput | null>();
  public emptyCart$ = new Subject<boolean>();
  public clearSearchForm$ = new Subject<boolean>();
  public clearResults$ = new Subject<boolean>();

  fetchLergData(input: Partial<LergSearchInput>): Observable<LergSearchResult> {
    return this.graphApi.watchQueryRef<LergSearchResult>({
      query: fetchLergDataQuery,
      variables: { input },
      fetchPolicy: 'network-only',
    }).getResult();
  }

  fetchIntlLocationData(input: Partial<IntlLocationSearchInput>): Observable<IntlLocationSearchResult> {
    return this.graphApi.watchQueryRef<IntlLocationSearchResult>({
      query: fetchIntlLocationDataQuery,
      variables: { input },
      fetchPolicy: 'network-only',
    }).getResult();
  }

  tnAvailableSearch(input: TnAvailableSearchInput): Observable<TnAvailableSearchResults> {
    return this.graphApi.watchQuery<TnAvailableSearchResults>({
      query: tnAvailableSearchQuery,
      variables: { input },
      fetchPolicy: 'no-cache',
    });
  }

  tnAvailableIntlSearch(input: TnAvailableSearchInput): Observable<TnAvailableSearchResults> {
    return this.graphApi.watchQuery<TnAvailableSearchResults>({
      query: tnAvailableIntlSearchQuery,
      variables: { input },
      fetchPolicy: 'no-cache',
    });
  }

  tnReserve(input: TnOrderingReserveInput): Observable<Apollo.MutateResult<any>> {
    return this.graphApi.mutate({
      mutation: tnReserve,
      variables: { input },
    });
  }

  tnRelease(input: TnOrderingReleaseInput): Observable<Apollo.MutateResult<any>> {
    return this.graphApi.mutate({
      mutation: tnRelease,
      variables: { input },
    });
  }

  tnOrder(input: TnOrderInput): Observable<Apollo.MutateResult<any>> {
    return this.graphApi.mutate({
      mutation: tnOrder,
      variables: { input },
    });
  }

  createTnChangeOrder(tns: string[], customerId?: number, portalType?: TnPortalType) {
    return this.graphApi
      .mutate({
        mutation: tnModifyTns,
        variables: {
          input: {
            tns,
            customerId,
            portalType
          }
        },
      })
      .pipe(
        tap((e) => {
          const orderId = e?.data?.tnModifyTns?.orderId;
          if (!orderId) return;
          this.router.navigateByUrl(`/number-provisioning/${orderId}/voice-features`);
        }),
        catchError((error) => {
          this.notificationService.addError(error);
          return of(null);
        })
      )
  }

  createTnDisonnectOrder(tns: string[]) {
    return this.graphApi
      .mutate({
        mutation: tnDisconnectTnsMultiAccount,
        variables: { input: { tns } },
      })
      .pipe(
        tap((e) => {
          const result = e?.data?.tnDisconnectTnsMultiAccount;
          const orderId = result && result.length !== 0 ? result[0].orderId : null;
          if (!orderId) return;
          this.router.navigateByUrl(`/number-provisioning/${orderId}/voice-features`);
        }),
        catchError((error) => {
          this.notificationService.addError(error);
          return of(null);
        })
      );
  }

  changeTnCustomer(fromCustomerId: number, toCustomerId: number, dids: string[], tnE164s: string[]) {
    return this.graphApi
    .mutate({
      mutation: cpaasTnsChangeCustomer,
      variables: {
        input: {
          fromCustomerId,
          toCustomerId,
          dids, // deprecated
          tnE164s
        }
      },
    })
    .pipe(
      tap((e) => {
        const result = e?.data?.cpaasTnsChangeCustomer;
        return result;
      }),
      catchError((error) => {
        this.notificationService.addError(error);
        return of(null);
      })
    );
  }
}
